import { useMemo, useRef } from "react";
import {
  Alert,
  Box,
  Container,
  IconButton,
  ThemeProvider,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  backLocation,
  getStepByLocation,
  screensDoneIn,
  STEPS,
} from "./steps/registry";
import { useFlowState } from "./state/FlowProvider";
import { useProgress } from "./progress/useProgress";
import ProgressBar from "./components/ProgressBar";
import { onboardingTokens, onboardingThemeOverrides } from "./theme";
import FlowErrorBoundary from "./components/FlowErrorBoundary";
import { useStepTransition } from "./useStepTransition";
import { t } from "../../locales/t";
import wordmark from "../../assets/images/Logo.svg";

const CONTENT_ID = "onboarding-content";

/**
 * Reads the two pieces of state the bar needs and renders it.
 *
 * Both come from the registry and the URL, so the bar is correct on its first
 * render. Nothing below reports upward: a step that routes internally — the
 * goal sub-flows — is located by its pathname, exactly as the step itself is.
 */
const FlowProgressBar = ({ step, pathname }) => {
  const state = useFlowState();
  const progress = useProgress(
    step.id,
    state,
    screensDoneIn(step, state, pathname),
  );

  return (
    <ProgressBar
      fraction={progress.fraction}
      stepNumber={progress.stepNumber}
      stepTotal={progress.stepTotal}
    />
  );
};

/**
 * The chrome every onboarding screen sits inside: brand mark, back control and
 * progress bar above a single content column.
 *
 * Steps render into the outlet and own nothing but their own content — no step
 * renders its own progress bar or its own header, so those cannot drift apart
 * from one screen to the next.
 */
const OnboardingLayout = () => {
  const state = useFlowState();
  const navigate = useNavigate();
  // `state` here is history state, not flow state. Done sets it when it sends a
  // member back over a refused completion, so the step that receives them can
  // say why without knowing anything about completion itself.
  const { pathname, state: locationState } = useLocation();

  const theme = useMemo(() => createTheme(onboardingThemeOverrides), []);

  const step = getStepByLocation(pathname);

  // Delegated to the registry, which owns sequence. A step may route internally
  // — the goal sub-flows are five paths under one step — so the whole pathname
  // goes with it and the layout stays ignorant of what a goal is.
  const previous = step ? backLocation(step, state, pathname) : null;

  const contentRef = useRef(null);
  // Keyed on the pathname, not the step id: moving between sub-flows is a real
  // navigation within one step, and keyed on the id alone focus, scroll and
  // title would never fire for it.
  useStepTransition(step?.id, contentRef, pathname);

  // The first step has nothing behind it and no progress to report yet. The
  // confirmation is the other end of the same idea: it is not a numbered step,
  // and its answers are already sent, so it offers no way back either.
  const showChrome =
    Boolean(step) &&
    step.id !== STEPS[0].id &&
    step.countsTowardProgress !== false;

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: onboardingTokens.background,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Visible only once focused, which is the convention: a keyboard
              user reaches it with one Tab from the top and skips the header,
              while it stays out of everyone else's way. */}
        <Box
          component="a"
          href={`#${CONTENT_ID}`}
          sx={{
            position: "absolute",
            left: -9999,
            top: 8,
            zIndex: (t2) => t2.zIndex.tooltip,
            px: 2,
            py: 1,
            borderRadius: onboardingTokens.radius,
            backgroundColor: onboardingTokens.card,
            color: onboardingTokens.navy,
            "&:focus": { left: 8 },
          }}
        >
          {t("onboarding.layout.skipToContent")}
        </Box>

        {/* Sticky so the progress bar stays visible on a long screen — the
              sub-flows scroll, and a bar that scrolls away stops answering the
              question it exists to answer. Opaque background rather than a
              translucent one so content passing underneath cannot make the bar
              or the wordmark hard to read. */}
        <Box
          component="header"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: (t2) => t2.zIndex.appBar,
            backgroundColor: onboardingTokens.background,
            borderBottom: `1px solid ${onboardingTokens.track}`,
          }}
        >
          <Container maxWidth="sm" sx={{ pt: 2, pb: showChrome ? 2 : 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minHeight: 44,
              }}
            >
              {showChrome && previous && (
                <IconButton
                  aria-label={t("onboarding.layout.back")}
                  onClick={() => navigate(`/onboarding/${previous.path}`)}
                  sx={{ color: onboardingTokens.navy, ml: -1 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              {/* Centred regardless of whether a back control is present, so
                    the mark does not shift horizontally between steps. */}
              <Box
                component="img"
                src={wordmark}
                alt={t("onboarding.layout.logoAlt")}
                sx={{
                  height: { xs: 26, sm: 32 },
                  width: "auto",
                  mx: "auto",
                  // Balance the back button's width so the wordmark stays
                  // optically centred instead of nudged right.
                  pr: showChrome && previous ? "40px" : 0,
                }}
              />
            </Box>

            {showChrome && step && (
              <Box sx={{ mt: 1.5 }}>
                <FlowProgressBar step={step} pathname={pathname} />
              </Box>
            )}
          </Container>
        </Box>

        <Container
          maxWidth="sm"
          sx={{
            pt: 4,
            pb: {
              xs: "calc(7rem + env(safe-area-inset-bottom))",
              sm: 6,
            },
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/*
           * Optical centring, biased upward.
           *
           * Two spacers sharing the leftover height in a 2:3 ratio, which
           * settles the content around 40% down rather than at 50%. True
           * centring makes a short screen look adrift and pushes the primary
           * action further from the thumb; leaving it flush under the header
           * leaves an empty lower half. This sits between the two.
           *
           * Ratio rather than a fixed offset so it holds at any window
           * height, and the spacers simply collapse to nothing once the
           * content is taller than the viewport — so the long screens are
           * completely unaffected.
           */}
          <Box sx={{ flexGrow: 2, flexShrink: 1, minHeight: 0 }} aria-hidden />
          {/* The step id, readable without knowing which screen is mounted.
                Tests assert the sequence against it rather than against screen
                internals, so a matrix scenario keeps passing when a placeholder
                is replaced by the real screen. Also the fastest way to see
                where a stuck flow actually is.

                tabIndex -1 so focus can be moved here on each step change; it
                is not in the tab order, only programmatically focusable. */}
          <Box
            component="main"
            id={CONTENT_ID}
            ref={contentRef}
            tabIndex={-1}
            aria-label={t("onboarding.layout.contentLabel")}
            data-onboarding-step={step?.id ?? "unknown"}
            // Never shrinks. It sits between two flexible spacers, and
            // without this a screen taller than the viewport would be
            // compressed and clipped rather than pushing the spacers to zero
            // and scrolling.
            sx={{ outline: "none", flexShrink: 0 }}
          >
            {/* Inside the content region, so the focus move on arrival puts a
                  screen reader on it rather than past it. */}
            {locationState?.completionFailed && (
              <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                {t("onboarding.done.rejected")}
              </Alert>
            )}

            <FlowErrorBoundary resetKey={step?.id}>
              <Outlet />
            </FlowErrorBoundary>
          </Box>

          <Box sx={{ flexGrow: 3, flexShrink: 1, minHeight: 0 }} aria-hidden />
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default OnboardingLayout;
